import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { X, Send, Loader2, BookOpen } from 'lucide-react';
import { notifyAdmins } from '../utils/notificationService';
import { useToast } from '../context/ToastContext';

export default function RequestCourseModal({ isOpen, onClose }) {
  const { currentUser, userData } = useAuth();
  const [courseName, setCourseName] = useState('');
  const [motivation, setMotivation] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { addToast } = useToast();

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!courseName.trim()) {
      addToast('يرجى إدخال اسم الدورة', 'error');
      return;
    }

    if (!currentUser) {
      addToast('يرجى تسجيل الدخول أولاً لتقديم طلب', 'warning');
      return;
    }

    setIsSubmitting(true);

    try {
      const sName = userData?.nameAr || userData?.name || currentUser.displayName || 'مستخدم';
      await addDoc(collection(db, 'course_requests'), {
        studentId: currentUser.uid,
        studentName: sName,
        courseName: courseName.trim(),
        motivation: motivation.trim() || null,
        requestedAt: serverTimestamp(),
        status: 'pending'
      });

      // Notify admins
      await notifyAdmins(
        'طلب دورة جديد',
        `قام الطالب ${sName} بطلب دورة جديدة بعنوان: ${courseName.trim()}`,
        'info',
        '/admin/requests'
      );

      addToast('تم إرسال طلبك بنجاح!', 'success');

      setCourseName('');
      setMotivation('');
      onClose();
    } catch (err) {
      console.error('Error submitting course request:', err);
      addToast('حدث خطأ أثناء إرسال الطلب. يرجى المحاولة مرة أخرى.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" dir="rtl">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-md bg-white dark:bg-zinc-900 rounded-3xl overflow-hidden shadow-2xl border border-slate-200 dark:border-white/10"
        >
          {/* Top Decorative Gradient */}
          <div className="h-2 bg-gradient-to-r from-primary-600 to-primary-400"></div>

          <button
            onClick={onClose}
            className="absolute top-5 left-5 p-2 bg-slate-100 dark:bg-white/5 hover:bg-rose-100 dark:hover:bg-rose-500/20 text-slate-500 dark:text-zinc-400 hover:text-rose-600 dark:hover:text-rose-400 rounded-xl transition-all"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="p-8">
            <div className="w-16 h-16 bg-primary-50 dark:bg-primary-500/10 rounded-2xl flex items-center justify-center mb-6 border border-primary-100 dark:border-primary-500/20">
              <BookOpen className="w-8 h-8 text-primary-600 dark:text-primary-400" />
            </div>

            <h2 className="text-2xl font-black text-slate-900 dark:text-white mb-2">طلب دورة جديدة</h2>
            <p className="text-slate-500 dark:text-zinc-400 font-medium mb-8 leading-relaxed">
              لم تجد الدورة التي تبحث عنها؟ أخبرنا بما تحتاجه وسنعمل على توفيرها قريباً!
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-bold text-slate-700 dark:text-zinc-300 mb-2">
                  اسم الدورة أو الموضوع <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  value={courseName}
                  onChange={(e) => setCourseName(e.target.value)}
                  placeholder="مثال: دورة متقدمة في الذكاء الاصطناعي..."
                  className="w-full px-5 py-4 bg-slate-50 dark:bg-zinc-950 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 text-slate-900 dark:text-white font-medium transition-all"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 dark:text-zinc-300 mb-2">
                  ما هي المواضيع المحددة التي تود تعلمها؟ <span className="text-slate-400 font-normal">(اختياري)</span>
                </label>
                <textarea
                  value={motivation}
                  onChange={(e) => setMotivation(e.target.value)}
                  placeholder="أخبرنا بالمواضيع والأدوات التي تود أن تغطيها الدورة..."
                  className="w-full px-5 py-4 bg-slate-50 dark:bg-zinc-950 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 text-slate-900 dark:text-white font-medium transition-all resize-none h-28"
                  disabled={isSubmitting}
                ></textarea>
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isSubmitting || !courseName.trim()}
                  className="w-full flex items-center justify-center gap-2 py-4 bg-primary-600 hover:bg-primary-500 active:bg-primary-700 text-white rounded-xl font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-primary-600/20"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      إرسال الطلب <Send className="w-5 h-5" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
